/**
 * Accessibility — WCAG 2.1 AA conformance + VPAT request page.
 *
 * Routes at /accessibility. Surfaces:
 *  - Ekselens' WCAG 2.1 AA conformance statement
 *  - A "Request our VPAT" lead-capture form (procurement gate for >£50k deals)
 *
 * The VPAT request submits to the existing /api/contact endpoint with
 * `source: "vpat"` and `area_of_interest: "Accessibility VPAT request"` so
 * it surfaces alongside other contacts in the admin queue.
 */
import React, { useState } from "react";
import { Shield, Check, Mail } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import StarMotif from "../components/StarMotif";
import InfoTooltip from "../components/admin/InfoTooltip";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRINCIPLES = [
  { code: "1", name: "Perceivable", note: "Text alternatives, captions, sufficient colour contrast, resizable text up to 200%." },
  { code: "2", name: "Operable", note: "Keyboard-accessible, no seizure-inducing motion, clear navigation, focus visible." },
  { code: "3", name: "Understandable", note: "Readable language, predictable interactions, input assistance and error identification." },
  { code: "4", name: "Robust", note: "Compatible with assistive technologies; valid HTML semantics; ARIA where needed." },
];

export default function Accessibility() {
  const [form, setForm] = useState({
    full_name: "", email: "", organisation: "", role: "",
    message: "Please send us your latest VPAT/ACR for procurement review.",
    consent: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onChange = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      toast.error("Please confirm consent to be contacted.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        ...form,
        area_of_interest: "Accessibility VPAT request",
        source: "vpat",
      });
      setSubmitted(true);
      toast.success("VPAT request received — we'll be in touch within 2 working days.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <article data-testid="accessibility-page">
      {/* Hero */}
      <section className="relative bg-black text-white overflow-hidden">
        <div className="absolute -right-32 -top-20 opacity-20 pointer-events-none">
          <StarMotif size={520} color="#7737F6" />
        </div>
        <div className="ek-container py-20 md:py-28 relative">
          <div className="ek-eyebrow text-[#A179FA] inline-flex items-center gap-2">
            <Shield size={12} /> Accessibility · WCAG 2.1 AA
          </div>
          <h1 className="ek-heading text-4xl sm:text-5xl lg:text-6xl mt-4 max-w-4xl">
            We design for everyone — and we&rsquo;ll show you the receipts.
          </h1>
          <p className="text-zinc-300 text-base md:text-lg mt-6 max-w-2xl">
            Ekselens Consulting commits to WCAG 2.1 Level AA conformance across our
            public site, assessments and client deliverables. Need our VPAT/ACR for
            procurement? Request it below — typical turnaround is 2 working days.
          </p>
          <div className="mt-8 inline-flex items-center gap-3 border border-white/30 px-4 py-2.5">
            <Shield size={16} className="text-[#A179FA]" />
            <span className="text-[11.5px] tracking-[0.16em] uppercase text-white/85" data-testid="accessibility-wcag-badge">
              WCAG 2.1 Level AA · target conformance
            </span>
          </div>
        </div>
      </section>

      {/* Principles */}
      <section className="bg-white">
        <div className="ek-container py-16 md:py-20">
          <div className="ek-eyebrow text-[#7737F6]">The four principles</div>
          <h2 className="ek-heading text-3xl mt-2">How we measure conformance</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 mt-10">
            {PRINCIPLES.map((p) => (
              <div key={p.code} className="border-l-2 border-[#7737F6] pl-6 py-2" data-testid={`accessibility-principle-${p.code}`}>
                <div className="ek-eyebrow text-[#7737F6]">{p.code}</div>
                <h3 className="ek-heading text-xl mt-1">{p.name}</h3>
                <p className="text-zinc-600 text-[14px] mt-2 leading-relaxed">{p.note}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VPAT form */}
      <section className="bg-[#F2F2F3]" id="vpat">
        <div className="ek-container py-16 md:py-20">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-10">
            <div className="md:col-span-2">
              <div className="ek-eyebrow text-[#7737F6]">Procurement-ready</div>
              <h2 className="ek-heading text-3xl mt-2">Request our VPAT / ACR</h2>
              <p className="text-zinc-600 text-[14px] mt-4 leading-relaxed">
                Voluntary Product Accessibility Template &amp; Accessibility Conformance
                Report — standard documentation procurement reviewers expect at
                contract size &gt;&pound;50k. We&rsquo;ll send our latest signed copy
                within 2 working days.
              </p>
              <ul className="mt-6 space-y-2 text-[13.5px] text-zinc-700">
                <li className="flex items-start gap-2"><Check size={14} className="mt-0.5 text-[#7737F6] shrink-0" /> Aligned to WCAG 2.1 AA criteria</li>
                <li className="flex items-start gap-2"><Check size={14} className="mt-0.5 text-[#7737F6] shrink-0" /> Section 508 mapping included</li>
                <li className="flex items-start gap-2"><Check size={14} className="mt-0.5 text-[#7737F6] shrink-0" /> Updated quarterly</li>
              </ul>
            </div>
            <div className="md:col-span-3">
              {submitted ? (
                <div className="bg-white border border-emerald-300 p-8" data-testid="accessibility-vpat-success">
                  <div className="ek-eyebrow text-emerald-700 inline-flex items-center gap-2">
                    <Check size={12} /> Request received
                  </div>
                  <h3 className="ek-heading text-2xl mt-2">Thank you, {form.full_name.split(" ")[0]}.</h3>
                  <p className="text-zinc-700 text-[14px] mt-3 leading-relaxed">
                    A signed copy of our VPAT/ACR will reach <strong>{form.email}</strong> within
                    2 working days. If you need it faster, reply to the confirmation email
                    and we&rsquo;ll prioritise.
                  </p>
                </div>
              ) : (
                <form onSubmit={submit} className="bg-white border border-zinc-200 p-8 space-y-5" data-testid="accessibility-vpat-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <label className="block text-[11px] tracking-[0.14em] uppercase text-zinc-500">
                      Full name *
                      <input
                        required type="text" value={form.full_name} onChange={onChange("full_name")}
                        maxLength={200}
                        className="mt-2 w-full border border-zinc-300 px-3 py-2.5 text-[14px] focus:outline-none focus:border-[#7737F6] normal-case tracking-normal text-black"
                        data-testid="accessibility-vpat-name"
                      />
                    </label>
                    <label className="block text-[11px] tracking-[0.14em] uppercase text-zinc-500">
                      Work email *
                      <input
                        required type="email" value={form.email} onChange={onChange("email")}
                        maxLength={200}
                        className="mt-2 w-full border border-zinc-300 px-3 py-2.5 text-[14px] focus:outline-none focus:border-[#7737F6] normal-case tracking-normal text-black"
                        data-testid="accessibility-vpat-email"
                      />
                    </label>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <label className="block text-[11px] tracking-[0.14em] uppercase text-zinc-500">
                      Organisation *
                      <input
                        required type="text" value={form.organisation} onChange={onChange("organisation")}
                        maxLength={200}
                        className="mt-2 w-full border border-zinc-300 px-3 py-2.5 text-[14px] focus:outline-none focus:border-[#7737F6] normal-case tracking-normal text-black"
                        data-testid="accessibility-vpat-org"
                      />
                    </label>
                    <label className="block text-[11px] tracking-[0.14em] uppercase text-zinc-500">
                      Role *
                      <InfoTooltip testid="vpat-role-info">
                        Your role in the procurement or accessibility review (e.g. Procurement Lead, Accessibility Officer). Helps us address the VPAT cover note correctly.
                      </InfoTooltip>
                      <input
                        required type="text" value={form.role} onChange={onChange("role")}
                        maxLength={200}
                        className="mt-2 w-full border border-zinc-300 px-3 py-2.5 text-[14px] focus:outline-none focus:border-[#7737F6] normal-case tracking-normal text-black"
                        data-testid="accessibility-vpat-role"
                      />
                    </label>
                  </div>
                  <label className="block text-[11px] tracking-[0.14em] uppercase text-zinc-500">
                    Notes (optional)
                    <InfoTooltip testid="vpat-notes-info">
                      Tell us which standard you need conformance against (WCAG 2.1/2.2, Section 508, EN 301 549) and any deadline — we'll prioritise the right ACR format.
                    </InfoTooltip>
                    <textarea
                      value={form.message} onChange={onChange("message")} rows={3}
                      maxLength={5000}
                      className="mt-2 w-full border border-zinc-300 px-3 py-2.5 text-[14px] focus:outline-none focus:border-[#7737F6] normal-case tracking-normal text-black"
                      data-testid="accessibility-vpat-message"
                    />
                  </label>
                  <label className="flex items-start gap-3 text-[12.5px] text-zinc-600">
                    <input
                      type="checkbox" checked={form.consent}
                      onChange={(e) => setForm((p) => ({ ...p, consent: e.target.checked }))}
                      className="mt-1"
                      data-testid="accessibility-vpat-consent"
                    />
                    <span>
                      I consent to Ekselens Consulting contacting me about this VPAT
                      request and storing the details above. We won&rsquo;t share them.
                    </span>
                  </label>
                  <button
                    type="submit" disabled={submitting}
                    className="ek-btn bg-[#7737F6] text-white text-[12px] tracking-[0.16em] uppercase px-7 py-3.5 inline-flex items-center gap-2 disabled:opacity-60"
                    data-testid="accessibility-vpat-submit"
                  >
                    <Mail size={13} /> {submitting ? "Sending…" : "Request VPAT"}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </article>
  );
}
